import { useState } from "react";
import { calculatedHint, computeCalculated, DEFAULT_FORMULA_DECIMALS, type FormulaAnalyte, formulaCodeKey, formulaNumber, type FormulaProblem, parseFormula } from "@probetalab/contracts";
import { fmtNumber } from "../lib/format.js";
import { Button, Field, Input, Select } from "./ui.js";

/**
 * The formula of a calculated analyte in the catalogue editor (ADR 0023): written over the codes
 * of this study's analytes, checked as it is typed (unknown code, syntax, circular reference —
 * the same `checkStudyFormulas` the server runs on save), and tried on sample values before saving.
 */

export const FORMULA_HELP = "Usa las claves de este estudio y + − * / ^ ( ). Funciones: si, min, max, abs, redondear, raiz, ln, log, exp. Del paciente: edad_anios, sexo.";

export function FormulaField({
  code,
  formula,
  decimals,
  study,
  problem,
  where,
  removable = true,
  onChange,
}: {
  code: string;
  formula: string | null;
  decimals: number | null;
  /** The study as being edited, for the preview of formulas that read other calculated analytes. */
  study: FormulaAnalyte[];
  problem: FormulaProblem | null;
  /** A branch's own formula (ADR 0032): its name goes in the accessible names; the branch's select removes it, not a button. */
  where?: string;
  removable?: boolean;
  onChange: (formula: string | null) => void;
}) {
  const [sample, setSample] = useState<Record<string, string>>({});
  const [patient, setPatient] = useState<{ edad: string; sexo: "" | "M" | "F" }>({ edad: "", sexo: "" });
  if (formula === null) {
    return (
      <div>
        <Button size="sm" variant="ghost" icon="calc" onClick={() => onChange("")}>
          Calcular con fórmula
        </Button>
      </div>
    );
  }
  const parsed = parseFormula(formula);
  // Inputs to try: the codes it reads, and through calculated ones the codes those read.
  const byCode = new Map(study.map((a) => [formulaCodeKey(a.code), a]));
  const inputs: string[] = [];
  const visit = (src: string | null, seen: Set<string>) => {
    const p = src ? parseFormula(src) : null;
    if (!p?.ok) return;
    for (const c of p.formula.codes) {
      const dep = byCode.get(c);
      if (dep?.formula && !seen.has(c)) visit(dep.formula, new Set([...seen, c]));
      else if (!inputs.includes(c)) inputs.push(c);
    }
  };
  visit(formula, new Set([formulaCodeKey(code)]));
  const needs = parsed.ok ? parsed.formula.patient : [];
  const self = { id: "__self", code, kind: "numeric", decimals, formula };
  const preview = !problem && parsed.ok
    ? computeCalculated([...study.filter((a) => formulaCodeKey(a.code) !== formulaCodeKey(code)), self], (c) => formulaNumber(sample[c]), {
        edad_anios: formulaNumber(patient.edad),
        sexo: patient.sexo || null,
      }).get("__self")
    : undefined;
  const result = preview?.value == null ? null : fmtNumber(preview.value, decimals ?? DEFAULT_FORMULA_DECIMALS);
  return (
    <div className="stack" style={{ gap: 8, paddingLeft: 10, borderLeft: "3px solid var(--brand-tint)" }}>
      <div className="row" style={{ gap: 8, alignItems: "flex-end" }}>
        <Field label="Fórmula" hint={FORMULA_HELP} error={problem ? problem.message : null} className="grow">
          <Input
            aria-label={`Fórmula de ${code || "este analito"}${where ? ` en ${where}` : ""}`}
            aria-invalid={problem ? true : undefined}
            className="mono"
            placeholder="Ej. URE / 2.1428"
            value={formula}
            onChange={(e) => onChange(e.target.value)}
          />
        </Field>
        {removable && (
          <Button size="sm" variant="ghost" icon="x" onClick={() => onChange(null)}>
            Quitar fórmula
          </Button>
        )}
      </div>
      {!problem && parsed.ok && (
        <div className="row wrap" style={{ gap: 8, alignItems: "flex-end" }} aria-label="Probar la fórmula">
          <strong className="small muted" style={{ alignSelf: "center" }}>Probar:</strong>
          {inputs.map((c) => (
            <Field key={c} label={c}>
              <Input aria-label={`Valor de prueba de ${c}${where ? ` en ${where}` : ""}`} inputMode="decimal" style={{ width: 90 }} value={sample[c] ?? ""} onChange={(e) => setSample((s) => ({ ...s, [c]: e.target.value }))} />
            </Field>
          ))}
          {needs.includes("edad_anios") && (
            <Field label="Edad (años)">
              <Input aria-label="Edad de prueba" inputMode="numeric" style={{ width: 80 }} value={patient.edad} onChange={(e) => setPatient((p) => ({ ...p, edad: e.target.value }))} />
            </Field>
          )}
          {needs.includes("sexo") && (
            <Field label="Sexo">
              <Select aria-label="Sexo de prueba" style={{ width: 120 }} value={patient.sexo} onChange={(e) => setPatient((p) => ({ ...p, sexo: e.target.value as "" | "M" | "F" }))}>
                <option value="">—</option>
                <option value="F">Mujer</option>
                <option value="M">Hombre</option>
              </Select>
            </Field>
          )}
          <span className="small" role="status" style={{ alignSelf: "center" }}>
            = <strong>{result ?? "—"}</strong>
            {result === null && preview && <span className="faint"> · {calculatedHint(preview)}</span>}
          </span>
        </div>
      )}
    </div>
  );
}
